import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Search, RefreshCw, User, Clock, Pill, ShoppingBag, Trash2, Plus, Printer,
    Send, ShieldAlert, MapPin, AlertTriangle, CheckCircle2, X, Repeat, Loader2,
    Keyboard, Store as StoreIcon, Ban, Sparkles,
} from 'lucide-react';
import PrescriptionOcrPanel from '../../components/ai/PrescriptionOcrPanel';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import { hospitalPatientCode } from '../../utils/patientCode';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { formatTime } from '../../utils/clock';
// A FACTORY, not a hook, despite the filename — imported under its real name so
// react-hooks/rules-of-hooks doesn't treat every call site as a misplaced hook.
// See the note at the top of hooks/usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';

const MySwal = withReactContent(Swal);

// THE DISPENSING COUNTER.
//
// One screen, two panes, and a queue of people standing behind whoever is using
// it. Everything below is shaped by that last fact: the pharmacist is not
// browsing, they are picking six items off a shelf, keying quantities, taking
// money and calling the next patient — with a hand on the keyboard the whole
// time. So the layout follows the physical job (queue on the left, the walk down
// the aisle on the right), the bin code is the loudest thing on a line because
// it is what the eye needs first, and every step of the sale has a key.
//
// WHY THE TOTALS ARE COMPUTED HERE AS WELL AS ON THE SERVER.
// The pharmacist quotes a figure before pressing Confirm, so the screen has to
// know it. That means the GST arithmetic exists twice, which is a duplication
// worth being honest about: the copy below mirrors services/pharmacy/
// billingService.js line for line — same paise integers, same tax-EXTRACTION
// from a tax-inclusive MRP, same half-away-from-zero split of an odd paisa — and
// it is a PREVIEW ONLY. What is stored and printed is always what the server
// computed; nothing here is ever posted as an amount. If the two ever disagree,
// the server is right and this file is the bug.
//
// TENANCY, PERMISSIONS AND FEATURE GATES ARE NOT THIS FILE'S JOB. The routes are
// gated by requireTenantFeature('pharmacy') + requirePermission, and the model
// layer scopes every read to one hospital. The `can()` checks below only decide
// whether to draw a control — a pharmacist without pharmacy.discount who edits
// the DOM still gets a 403 from the server.

const QUEUE_REFRESH_MS = 20000;
const QUEUE_PAGE_SIZE = 40;
const SEARCH_DEBOUNCE_MS = 350;
const STORE_STORAGE_KEY = 'pharmacyCounterStoreId';
// A pack within three months of its expiry still sells, but the pharmacist
// should see it coming — it is the difference between "take this one" and "take
// this one first".
const EXPIRY_WARNING_DAYS = 90;

const PAYMENT_METHODS = ['cash', 'upi', 'card', 'insurance'];
// A reference is what makes a non-cash payment reconcilable at the end of the
// shift: cash is in the drawer and counts itself, a UPI transaction is only
// findable by its id.
const METHODS_NEEDING_REFERENCE = ['upi', 'card', 'insurance'];

/* ------------------------------------------------------------------ *
 * Money
 *
 * All of it in integer paise. The moment a rupee figure passes through a float
 * the two halves of a GST split stop adding back up to the tax charged, and the
 * bill on screen disagrees with the bill in the patient's hand by a paisa.
 * ------------------------------------------------------------------ */

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const paise = (v) => Math.round(toNum(v) * 100);
const money = (v) => toNum(v).toFixed(2);

/**
 * Mirror of billingService.computeLine.
 *
 * MRP IS TAX-INCLUSIVE, so tax is extracted from the line value rather than
 * added to it, and an INPATIENT line is exempt entirely — medicines supplied
 * during an admission are part of a composite healthcare supply, so the rate is
 * forced to zero whatever the drug master says.
 *
 * Unlike the server it CLAMPS an over-large discount instead of throwing, and
 * reports it as `overDiscount`: the pharmacist is mid-keystroke and a screen
 * that blanks itself on the way to '150' is unusable. Confirm stays disabled
 * while the flag is set, so the refusal still happens — just in a place the
 * person can see and fix.
 */
function costLine({
    unitMrp, qty, discountPercent = 0, discountAmount = null,
    gstRate = 0, patientType = 'OP', isInterState = false,
}) {
    const unitMrpP = paise(unitMrp);
    const qtyU = paise(qty);
    const grossP = Math.round((unitMrpP * qtyU) / 100);

    // An explicitly keyed rupee amount beats the percentage, because that figure
    // is what the pharmacist actually promised the patient; re-deriving it from
    // a rounded percentage lands a paisa or two away from the conversation.
    const hasAmount = discountAmount !== null && discountAmount !== undefined && discountAmount !== '';
    const rawDiscountP = hasAmount
        ? paise(discountAmount)
        : Math.round((grossP * paise(discountPercent)) / 10000);
    const overDiscount = rawDiscountP > grossP;
    const discountP = Math.max(0, Math.min(rawDiscountP, grossP));

    const lineValueP = grossP - discountP;

    const isExempt = String(patientType || '').trim().toUpperCase() === 'IP';
    const rateBp = isExempt ? 0 : paise(gstRate);

    const taxableP = rateBp === 0 ? lineValueP : Math.round((lineValueP * 10000) / (10000 + rateBp));
    const taxP = lineValueP - taxableP;

    let cgstP = 0;
    let sgstP = 0;
    let igstP = 0;
    if (taxP !== 0) {
        if (isInterState) {
            igstP = taxP;
        } else {
            // The odd paisa has to land somewhere. Giving it to CGST and taking
            // SGST as the remainder keeps cgst + sgst identically equal to the
            // tax charged — rounding each half on its own does not.
            cgstP = Math.round(taxP / 2);
            sgstP = taxP - cgstP;
        }
    }

    return {
        grossValue: grossP / 100,
        discountAmount: discountP / 100,
        taxableAmount: taxableP / 100,
        gstRate: rateBp / 100,
        cgst: cgstP / 100,
        sgst: sgstP / 100,
        igst: igstP / 100,
        lineTotal: lineValueP / 100,
        overDiscount,
    };
}

/** Mirror of billingService.computeTotals. Round-off is its own disclosed line. */
function costTotals(costed) {
    let subP = 0; let discP = 0; let taxableP = 0;
    let cgstP = 0; let sgstP = 0; let igstP = 0; let netP = 0;
    for (const l of costed) {
        subP += paise(l.grossValue);
        discP += paise(l.discountAmount);
        taxableP += paise(l.taxableAmount);
        cgstP += paise(l.cgst);
        sgstP += paise(l.sgst);
        igstP += paise(l.igst);
        netP += paise(l.lineTotal);
    }
    // The drawer settles in whole rupees, so the round-off is derived from the
    // summed paise rather than from an already-rounded figure — that way it can
    // never exceed fifty paise.
    const grandP = Math.round(netP / 100) * 100;
    return {
        subTotal: subP / 100,
        discountTotal: discP / 100,
        taxableTotal: taxableP / 100,
        cgstTotal: cgstP / 100,
        sgstTotal: sgstP / 100,
        igstTotal: igstP / 100,
        roundOff: (grandP - netP) / 100,
        grandTotal: grandP / 100,
    };
}

/* ------------------------------------------------------------------ *
 * Small presentational pieces
 * ------------------------------------------------------------------ */

const fmtExpiry = (d) => {
    if (!d) return '—';
    const s = String(d);
    const [y, m] = s.split('-');
    return y && m ? `${m}/${y.slice(2)}` : s;
};

const daysUntil = (d) => {
    if (!d) return null;
    const then = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(then.getTime())) return null;
    return Math.floor((then - new Date()) / 86400000);
};

// The one clock format ("2:30 PM", utils/clock); '' when not a date.
const fmtTime = (iso) => formatTime(iso);

/* ------------------------------------------------------------------ *
 * Turning what the API returned into an editable line
 *
 * Outside the component because neither of these reads a single piece of state
 * — keeping them inside would make every callback that uses them re-created on
 * each keystroke, which at a counter is the one place that cost is felt.
 * ------------------------------------------------------------------ */

const newKey = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** A line added at the counter, from the drug-master type-ahead. */
function lineFromSearchHit(hit) {
    return {
        key: newKey(`c-${hit.medicineId}`),
        prescriptionId: null,
        source: 'counter',
        medicine: {
            medicineId: hit.medicineId,
            name: hit.name,
            genericName: hit.genericName,
            brand: hit.brand,
            form: hit.form,
            strength: hit.strength,
            unit: hit.unit,
            hsnCode: hit.hsnCode,
            gstRate: toNum(hit.gstRate),
            schedule: hit.schedule || 'none',
        },
        prescribed: null,
        // The batch's own bin beats the medicine's home bin: a cold-chain or
        // overflow batch is physically somewhere else, and the home bin would be
        // a wrong answer delivered confidently.
        binCode: hit.pickBinCode || hit.homeBinCode || null,
        binType: hit.homeBinType || null,
        qtyOnHand: toNum(hit.qtySellable),
        suggestedBatch: hit.suggestedBatch || null,
        batchId: '',
        batches: null,
        batchesState: 'idle',
        qtyPrescribed: '1',
        qtyDispensed: '1',
        discountPercent: '',
        discountAmount: '',
        // Which of the two the pharmacist is keying. Both columns exist because
        // a flat rupee figure is what gets promised across the counter ("make it
        // 500") and re-deriving it from a rounded percentage lands a paisa away
        // from the conversation — so whichever was typed is the one sent.
        discountMode: 'pct',
        substitutedFromMedicineId: null,
        alreadyDispensed: 0,
        fullCourseQty: null,
        matchedOnGeneric: false,
        unmatched: false,
        serverError: null,
    };
}

/** A line the doctor wrote, enriched by GET /pharmacy/prescription/:id. */
function lineFromPrescription(row) {
    // null when the doctor wrote free text rather than a morning-afternoon-
    // evening-night schedule: there is nothing to multiply out, so no quantity
    // is suggested and the pharmacist keys both figures rather than being handed
    // a guess dressed up as the prescription.
    const fullCourse = row.suggestedQty === null || row.suggestedQty === undefined
        ? null
        : toNum(row.suggestedQty);
    const already = toNum(row.qtyAlreadyDispensed);
    const remaining = fullCourse === null ? null : Math.max(0, fullCourse - already);
    const available = toNum(row.qtyOnHand);

    return {
        key: `p-${row.prescriptionId}`,
        prescriptionId: row.prescriptionId,
        source: 'prescription',
        medicine: row.medicine ? { ...row.medicine, gstRate: toNum(row.medicine.gstRate) } : null,
        prescribed: row.prescribed || null,
        binCode: row.location?.code || null,
        binType: row.location?.type || null,
        qtyOnHand: available,
        suggestedBatch: row.suggestedBatch || null,
        batchId: '',
        batches: null,
        batchesState: 'idle',
        // WHY "PRESCRIBED" DEFAULTS TO WHAT IS STILL OWED RATHER THAN TO THE
        // WHOLE COURSE. A patient collecting the balance of a part-filled
        // prescription is COMPLETING it. If this dispense claimed the full
        // course as its prescribed quantity, handing over the remainder would be
        // recorded as another partial — and the server keeps a partially
        // dispensed prescription on the queue, so it would sit there for ever
        // with nobody able to clear it. The full course stays visible beside the
        // field as context.
        qtyPrescribed: remaining === null ? '' : String(remaining),
        qtyDispensed: remaining === null ? '' : String(Math.max(0, Math.min(remaining, available))),
        discountPercent: '',
        discountAmount: '',
        discountMode: 'pct',
        substitutedFromMedicineId: null,
        alreadyDispensed: already,
        fullCourseQty: fullCourse,
        matchedOnGeneric: !!row.matchedOnGeneric,
        unmatched: !row.medicine,
        serverError: null,
    };
}

/**
 * The bin. Deliberately the loudest element on a line and deliberately not in
 * the brand palette: it is a wayfinding label, not chrome, and it has to win
 * against a row that also carries a name, a batch, a quantity and a price. Near
 * black on white beats teal on white for that, and it keeps the teal meaning
 * "this is the app" rather than "this is important".
 */
function BinBadge({ code, type, testId }) {
    const { t } = useTranslation();
    if (!code) {
        return (
            <div
                data-testid={testId}
                title={t('pharmacycounter.no_bin_hint', 'No bin recorded for this medicine — it will need a hunt.')}
                className="flex flex-col items-center justify-center min-w-[86px] px-2 py-2 rounded-xl bg-amber-100 border-2 border-dashed border-amber-400 text-amber-700"
            >
                <MapPin className="w-4 h-4" />
                <span className="text-[10px] font-bold uppercase tracking-wide mt-0.5">
                    {t('pharmacycounter.no_bin', 'No bin')}
                </span>
            </div>
        );
    }
    return (
        <div
            data-testid={testId}
            className="flex flex-col items-center justify-center min-w-[86px] px-3 py-2 rounded-xl bg-slate-900 text-white shadow-md"
        >
            <span className="font-mono text-2xl leading-none font-black tracking-widest">{code}</span>
            {type && type !== 'shelf' && (
                <span className="text-[9px] uppercase tracking-wider text-[#4ABDBD] mt-1 font-semibold">
                    {String(type).replace(/_/g, ' ')}
                </span>
            )}
        </div>
    );
}

/** Schedule H / H1 / X. Anything but 'none' is prescription-only. */
function ScheduleBadge({ schedule }) {
    if (!schedule || schedule === 'none') return null;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-[11px] font-bold uppercase tracking-wide">
            <ShieldAlert className="w-3 h-3" />
            {`Sch ${schedule}`}
        </span>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function PharmacyCounter() {
    const { t } = useTranslation();
    const { can } = usePermissions();

    // One memoised instance: it reads the access token inside its request
    // interceptor, so a single copy stays correct across a token refresh.
    const api = useMemo(() => createAuthedAxios(), []);

    const canDiscount = can('pharmacy.discount');
    const canSubstitute = can('pharmacy.substitute');

    /* ---- counters ---- */
    const [stores, setStores] = useState([]);
    const [storeId, setStoreId] = useState(() => localStorage.getItem(STORE_STORAGE_KEY) || '');
    const [storesLoading, setStoresLoading] = useState(true);

    /* ---- AI pack: prescription OCR (Phase 4, #15) ---- */
    const { has: hasFeature } = useTenantFeatures();
    const [showOcrPanel, setShowOcrPanel] = useState(false);

    /* ---- left pane: the queue ---- */
    const [queue, setQueue] = useState([]);
    const [queueLoading, setQueueLoading] = useState(true);
    const [queueError, setQueueError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [refreshedAt, setRefreshedAt] = useState(null);

    /* ---- right pane: the sale being built ---- */
    // 'prescription' fulfils a consultation; 'walkin' is an over-the-counter
    // sale to somebody who was never booked in.
    const [mode, setMode] = useState('prescription');
    const [selected, setSelected] = useState(null);      // the queue entry
    const [detail, setDetail] = useState(null);          // GET /prescription/:id
    const [detailLoading, setDetailLoading] = useState(false);
    const [lines, setLines] = useState([]);
    const [dirty, setDirty] = useState(false);

    const [walkIn, setWalkIn] = useState({ name: '', phone: '' });
    const [patientType, setPatientType] = useState('OP');
    const [isInterState, setIsInterState] = useState(false);
    const [placeOfSupply, setPlaceOfSupply] = useState('');

    const [payments, setPayments] = useState([{ method: 'cash', amount: '', reference: '' }]);
    const paymentsTouched = useRef(false);

    /* ---- the item picker (add an OTC item, or substitute a line) ---- */
    const [pickerQuery, setPickerQuery] = useState('');
    const [pickerResults, setPickerResults] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [substituteFor, setSubstituteFor] = useState(null);   // line key

    /* ---- the sale ---- */
    const [submitting, setSubmitting] = useState(false);
    const [bill, setBill] = useState(null);                     // POST /dispense result
    const [resending, setResending] = useState(false);

    const searchInputRef = useRef(null);
    const pickerInputRef = useRef(null);
    const qtyRefs = useRef({});
    const queueListRef = useRef(null);

    const hasPrescriptionRef = mode === 'prescription' && !!selected?.appointmentId;

    /* -------------------------------------------------------------- *
     * Counters
     * -------------------------------------------------------------- */

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/pharmacy/stores?status=active');
                if (cancelled) return;
                const list = res?.data?.data || [];
                setStores(list);
                // A remembered counter wins, because staff are posted to one and
                // work it all day; otherwise the store marked default, which the
                // list already sorts first.
                setStoreId((current) => {
                    if (current && list.some((s) => s.storeId === current)) return current;
                    return list[0]?.storeId || '';
                });
            } catch (err) {
                if (!cancelled) {
                    toast.warn(extractErrorMessage(err, t('pharmacycounter.stores_error', 'Could not load the pharmacy counters')));
                }
            } finally {
                if (!cancelled) setStoresLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [api, t]);

    useEffect(() => {
        if (storeId) localStorage.setItem(STORE_STORAGE_KEY, storeId);
    }, [storeId]);

    /* -------------------------------------------------------------- *
     * The queue
     * -------------------------------------------------------------- */

    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [searchQuery]);

    const loadQueue = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setQueueLoading(true);
        try {
            const qs = new URLSearchParams({ page: '1', limit: String(QUEUE_PAGE_SIZE) });
            if (debouncedSearch) qs.append('search', debouncedSearch);
            const res = await api.get(`/pharmacy/queue?${qs.toString()}`);
            setQueue(res?.data?.data || []);
            setQueueError(null);
            setRefreshedAt(new Date());
        } catch (err) {
            setQueueError(extractErrorMessage(err, t('pharmacycounter.queue_error', 'Could not load the dispensing queue')));
        } finally {
            if (!quiet) setQueueLoading(false);
        }
    }, [api, debouncedSearch, t]);

    useEffect(() => { loadQueue(); }, [loadQueue]);

    // The queue is a live list of people waiting, so it refreshes itself —
    // quietly, and without touching the right pane. A pharmacist halfway through
    // keying a bill must never have it move under them.
    useEffect(() => {
        const id = setInterval(() => loadQueue({ quiet: true }), QUEUE_REFRESH_MS);
        return () => clearInterval(id);
    }, [loadQueue]);

    useEffect(() => { setHighlight(0); }, [debouncedSearch]);

    /* -------------------------------------------------------------- *
     * Building the sale
     * -------------------------------------------------------------- */

    const resetSale = useCallback(() => {
        setLines([]);
        setPayments([{ method: 'cash', amount: '', reference: '' }]);
        paymentsTouched.current = false;
        setBill(null);
        setDirty(false);
        setSubstituteFor(null);
        setPickerQuery('');
        setPickerResults([]);
        // Both tax switches go back to the safe default for every new sale
        // rather than carrying over. Wrongly claiming the inpatient exemption or
        // an inter-state supply understates a liability, which is the error a
        // GST audit penalises; wrongly charging tax is visible on the face of
        // the bill and is corrected the same afternoon. So the sticky default
        // has to be the taxable one.
        setPatientType('OP');
        setIsInterState(false);
        setPlaceOfSupply('');
    }, []);

    const openPrescription = useCallback(async (entry) => {
        setMode('prescription');
        setSelected(entry);
        setDetail(null);
        resetSale();
        setDetailLoading(true);
        try {
            const qs = storeId ? `?storeId=${encodeURIComponent(storeId)}` : '';
            const res = await api.get(`/pharmacy/prescription/${entry.appointmentId}${qs}`);
            const data = res?.data?.data;
            setDetail(data);
            // Already sorted by pick sequence server-side — one walk down the
            // aisle instead of six crossings of the room. Left exactly as it
            // arrived, on purpose.
            setLines((data?.lines || []).map(lineFromPrescription));
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('pharmacycounter.prescription_error', 'Could not open that prescription')));
            setSelected(null);
        } finally {
            setDetailLoading(false);
        }
    }, [api, storeId, resetSale, t]);

    // Switching away throws keyed work away, so it asks first — but only once
    // something has actually been keyed. Confirming an empty screen is friction
    // for nothing.
    const guardedOpen = useCallback(async (entry) => {
        if (dirty && !bill) {
            const res = await MySwal.fire({
                title: t('pharmacycounter.discard_title', 'Leave this bill?'),
                text: t('pharmacycounter.discard_text', 'What you have keyed on this prescription will be lost. Nothing has been dispensed.'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('pharmacycounter.discard', 'Discard'),
                cancelButtonText: t('pharmacycounter.stay', 'Stay here'),
            });
            if (!res.isConfirmed) return;
        }
        openPrescription(entry);
    }, [dirty, bill, openPrescription, t]);

    const startWalkIn = useCallback(async () => {
        if (dirty && !bill) {
            const res = await MySwal.fire({
                title: t('pharmacycounter.discard_title', 'Leave this bill?'),
                text: t('pharmacycounter.discard_text', 'What you have keyed on this prescription will be lost. Nothing has been dispensed.'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('pharmacycounter.discard', 'Discard'),
                cancelButtonText: t('pharmacycounter.stay', 'Stay here'),
            });
            if (!res.isConfirmed) return;
        }
        setMode('walkin');
        setSelected(null);
        setDetail(null);
        setWalkIn({ name: '', phone: '' });
        resetSale();
        setTimeout(() => pickerInputRef.current?.focus(), 0);
    }, [dirty, bill, resetSale, t]);

    const patchLine = useCallback((key, patch) => {
        setDirty(true);
        setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch, serverError: null } : l)));
    }, []);

    const removeLine = useCallback((key) => {
        setDirty(true);
        setLines((prev) => prev.filter((l) => l.key !== key));
    }, []);

    /* -------------------------------------------------------------- *
     * Batches
     *
     * The suggestion the screen shows must be the batch the sale will actually
     * consume, because the bill's batch number is the only thing that answers
     * "who did we sell B7391 to" when a recall arrives. Leaving the selector on
     * "FEFO" means the server picks, which is the same rule; naming a batch
     * pins it.
     * -------------------------------------------------------------- */

    const loadBatchesFor = useCallback(async (medicineId, keys) => {
        if (!medicineId || !storeId) return;
        setLines((prev) => prev.map((l) => (keys.includes(l.key) ? { ...l, batchesState: 'loading' } : l)));
        try {
            const qs = new URLSearchParams({ storeId, medicineId, status: 'active', limit: '50' });
            const res = await api.get(`/pharmacy/batches?${qs.toString()}`);
            const rows = (res?.data?.data || []).filter((b) => toNum(b.qtyOnHand) > 0);
            setLines((prev) => prev.map((l) => (
                keys.includes(l.key) ? { ...l, batches: rows, batchesState: 'ready' } : l
            )));
        } catch (err) {
            // 403 is the expected answer for an ordinary counter operator: batch
            // rows carry purchaseRate, which is what the hospital PAID, and that
            // is gated on the stock permissions. It is not an error to report —
            // the pharmacist simply dispenses on FEFO, which is the correct
            // default anyway.
            const denied = err?.response?.status === 403;
            setLines((prev) => prev.map((l) => (
                keys.includes(l.key) ? { ...l, batches: [], batchesState: denied ? 'denied' : 'error' } : l
            )));
        }
    }, [api, storeId]);

    // Fetched as soon as the lines land rather than when a dropdown is opened:
    // a native select cannot fill itself after the list is already showing, and
    // a pharmacist who has to click twice to see the batches will stop looking.
    useEffect(() => {
        const pending = new Map();
        for (const l of lines) {
            if (l.batchesState !== 'idle' || !l.medicine?.medicineId) continue;
            const list = pending.get(l.medicine.medicineId) || [];
            list.push(l.key);
            pending.set(l.medicine.medicineId, list);
        }
        if (!pending.size) return;
        for (const [medicineId, keys] of pending) loadBatchesFor(medicineId, keys);
    }, [lines, loadBatchesFor]);

    /* -------------------------------------------------------------- *
     * The item picker
     * -------------------------------------------------------------- */

    useEffect(() => {
        const q = pickerQuery.trim();
        if (q.length < 2) { setPickerResults([]); return undefined; }
        let cancelled = false;
        setPickerLoading(true);
        const id = setTimeout(async () => {
            try {
                const qs = new URLSearchParams({ q, limit: '15' });
                if (storeId) qs.append('storeId', storeId);
                const res = await api.get(`/pharmacy/medicines/search?${qs.toString()}`);
                if (!cancelled) setPickerResults(res?.data?.data || []);
            } catch {
                if (!cancelled) setPickerResults([]);
            } finally {
                if (!cancelled) setPickerLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => { cancelled = true; clearTimeout(id); };
    }, [pickerQuery, storeId, api]);

    const choosePickerResult = (hit) => {
        setDirty(true);
        if (substituteFor) {
            setLines((prev) => prev.map((l) => {
                if (l.key !== substituteFor) return l;
                const fresh = lineFromSearchHit(hit);
                return {
                    ...l,
                    medicine: fresh.medicine,
                    binCode: fresh.binCode,
                    binType: fresh.binType,
                    qtyOnHand: fresh.qtyOnHand,
                    suggestedBatch: fresh.suggestedBatch,
                    batchId: '',
                    batches: null,
                    batchesState: 'idle',
                    // Only a line that HAD a drug-master row is a substitution.
                    // Filling in a line the master never matched is not swapping
                    // one brand for another, it is finding the product at all.
                    substitutedFromMedicineId: l.medicine?.medicineId || null,
                    unmatched: false,
                    serverError: null,
                };
            }));
            setSubstituteFor(null);
        } else {
            setLines((prev) => [...prev, lineFromSearchHit(hit)]);
        }
        setPickerQuery('');
        setPickerResults([]);
        pickerInputRef.current?.focus();
    };

    /* -------------------------------------------------------------- *
     * Pricing the sale on screen
     * -------------------------------------------------------------- */

    const priced = useMemo(() => lines.map((line) => {
        const chosen = line.batchId
            ? (line.batches || []).find((b) => b.batchId === line.batchId) || null
            : line.suggestedBatch;
        const unitMrp = chosen?.mrp;
        const qty = toNum(line.qtyDispensed);
        const available = line.batchId ? toNum(chosen?.qtyOnHand) : line.qtyOnHand;

        const cost = costLine({
            unitMrp: unitMrp ?? 0,
            qty,
            discountPercent: canDiscount ? line.discountPercent : 0,
            discountAmount: canDiscount ? line.discountAmount : null,
            gstRate: line.medicine?.gstRate || 0,
            patientType,
            isInterState,
        });

        // Every reason this line cannot leave the shelf, collected in one place
        // so the row can say so and Confirm can refuse. Order matters: the first
        // is what the pharmacist is shown.
        const blocks = [];
        if (!line.medicine) {
            blocks.push(t('pharmacycounter.block_unmatched', 'Not in the drug master — substitute it or add the product first'));
        } else if (line.medicine.schedule && line.medicine.schedule !== 'none' && !hasPrescriptionRef) {
            blocks.push(t(
                'pharmacycounter.block_schedule',
                'Schedule {{s}} — cannot be sold over the counter without a prescription',
                { s: line.medicine.schedule }
            ));
        }
        if (qty > 0) {
            if (unitMrp === null || unitMrp === undefined) {
                blocks.push(t('pharmacycounter.block_no_price', 'No priced stock — key the MRP from the pack before selling'));
            }
            if (available < qty) {
                blocks.push(t('pharmacycounter.block_stock', 'Only {{n}} on hand at this counter', { n: available }));
            }
            const prescribedQty = toNum(line.qtyPrescribed);
            if (prescribedQty > 0 && qty > prescribedQty) {
                blocks.push(t('pharmacycounter.block_over', 'More than was prescribed'));
            }
            if (cost.overDiscount) {
                blocks.push(t('pharmacycounter.block_discount', 'The discount is larger than the line value'));
            }
        }

        return { line, chosen, unitMrp, qty, available, cost, blocks, include: qty > 0 };
    }), [lines, canDiscount, patientType, isInterState, hasPrescriptionRef, t]);

    const included = useMemo(() => priced.filter((p) => p.include), [priced]);
    const totals = useMemo(() => costTotals(included.map((p) => p.cost)), [included]);
    const blockedCount = priced.filter((p) => p.blocks.length > 0 && p.include).length;

    // Keep the tender in step with the bill until somebody edits it. The common
    // case by far is one payment for the whole amount, and re-keying the figure
    // the screen just computed is wasted time at a counter.
    useEffect(() => {
        if (paymentsTouched.current) return;
        setPayments((prev) => (prev.length === 1
            ? [{ ...prev[0], amount: totals.grandTotal > 0 ? money(totals.grandTotal) : '' }]
            : prev));
    }, [totals.grandTotal]);

    const paidTotal = useMemo(
        () => payments.reduce((sum, p) => sum + toNum(p.amount), 0),
        [payments]
    );
    const overPaid = paise(paidTotal) > paise(totals.grandTotal);
    const balanceDue = Math.max(0, (paise(totals.grandTotal) - paise(paidTotal)) / 100);

    const patchPayment = (index, patch) => {
        paymentsTouched.current = true;
        setPayments((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    };

    const missingReference = payments.some(
        (p) => toNum(p.amount) > 0 && METHODS_NEEDING_REFERENCE.includes(p.method) && !String(p.reference || '').trim()
    );

    const walkInNamed = mode !== 'walkin' || String(walkIn.name || '').trim().length > 0;

    const canConfirm = !!storeId
        && included.length > 0
        && blockedCount === 0
        && !overPaid
        && !missingReference
        && walkInNamed
        && !submitting
        && !bill;

    /* -------------------------------------------------------------- *
     * Confirm
     * -------------------------------------------------------------- */

    const confirmDispense = useCallback(async () => {
        if (!canConfirm) return;
        setSubmitting(true);
        try {
            const payload = {
                storeId,
                appointmentId: mode === 'prescription' ? selected?.appointmentId || null : null,
                walkInName: mode === 'walkin' ? walkIn.name.trim() : null,
                walkInPhone: mode === 'walkin' ? walkIn.phone.trim() || null : null,
                patientType,
                isInterState,
                placeOfSupply: placeOfSupply.trim() || null,
                lines: included.map(({ line }) => ({
                    medicineId: line.medicine.medicineId,
                    // Empty means "let FEFO decide", which is the same rule the
                    // screen previewed — naming the batch only pins it.
                    batchId: line.batchId || null,
                    qtyPrescribed: toNum(line.qtyPrescribed) || toNum(line.qtyDispensed),
                    qtyDispensed: toNum(line.qtyDispensed),
                    // Amounts are never posted. The server costs the sale from
                    // the batch it actually consumed; sending a total would only
                    // be a figure it has to ignore.
                    discountPercent: canDiscount ? toNum(line.discountPercent) : 0,
                    discountAmount: canDiscount && line.discountAmount !== '' ? toNum(line.discountAmount) : null,
                    substitutedFromMedicineId: line.substitutedFromMedicineId || null,
                })),
                payments: payments
                    .filter((p) => toNum(p.amount) > 0)
                    .map((p) => ({ method: p.method, amount: toNum(p.amount), reference: p.reference.trim() || null })),
            };

            const res = await api.post('/pharmacy/dispense', payload);
            setBill(res?.data?.data || null);
            setDirty(false);
            toast.success(t('pharmacycounter.dispensed', 'Dispensed'));
            loadQueue({ quiet: true });
        } catch (err) {
            const code = err?.response?.data?.code;
            const detailData = err?.response?.data?.data;
            if (code === 'INSUFFICIENT_STOCK' && detailData?.medicineId) {
                // "Out of stock" alone is not actionable — how many there ARE is
                // what decides whether a partial goes home with the patient. So
                // the shortage is pinned to its own line with the real number.
                setLines((prev) => prev.map((l) => (
                    l.medicine?.medicineId === detailData.medicineId
                        ? {
                            ...l,
                            qtyOnHand: toNum(detailData.available),
                            serverError: t(
                                'pharmacycounter.short_stock',
                                'Only {{n}} left — key that quantity or take a partial',
                                { n: toNum(detailData.available) }
                            ),
                        }
                        : l
                )));
            }
            toast.warn(extractErrorMessage(err, t('pharmacycounter.dispense_error', 'The sale could not be completed — nothing has left the shelf')));
        } finally {
            setSubmitting(false);
        }
    }, [
        canConfirm, storeId, mode, selected, walkIn, patientType, isInterState,
        placeOfSupply, included, payments, canDiscount, api, t, loadQueue,
    ]);

    /* -------------------------------------------------------------- *
     * The bill
     * -------------------------------------------------------------- */

    const printBill = useCallback(async () => {
        const invoiceId = bill?.invoice?.invoiceId;
        if (!invoiceId) return;
        // The tab is opened SYNCHRONOUSLY, before the await. A window opened
        // after a promise resolves is no longer attributable to the click and
        // every popup blocker eats it — which at a counter looks like the print
        // button doing nothing.
        const tab = window.open('', '_blank');
        try {
            const res = await api.get(`/pharmacy/invoices/${invoiceId}/pdf`);
            const url = res?.data?.data?.url;
            if (!url) throw new Error('no url');
            if (tab) tab.location.href = url; else window.location.href = url;
        } catch (err) {
            if (tab) tab.close();
            toast.warn(extractErrorMessage(err, t('pharmacycounter.print_error', 'The bill PDF is not ready yet — try again in a moment')));
        }
    }, [bill, api, t]);

    const resendBill = useCallback(async (channel) => {
        const invoiceId = bill?.invoice?.invoiceId;
        if (!invoiceId) return;
        setResending(true);
        const send = async (recipient) => api.post(
            `/pharmacy/invoices/${invoiceId}/resend`,
            recipient ? { channel, recipient } : { channel }
        );
        try {
            const res = await send(null);
            toast.success(res?.data?.message || t('pharmacycounter.resent', 'A copy of the bill has been queued'));
        } catch (err) {
            if (err?.response?.data?.code === 'NO_RECIPIENT') {
                // Nothing on file. Asking is better than failing: at the counter
                // the patient is standing there and can read it out.
                const prompt = await MySwal.fire({
                    title: channel === 'email'
                        ? t('pharmacycounter.ask_email', 'Send to which email address?')
                        : t('pharmacycounter.ask_phone', 'Send to which number?'),
                    input: channel === 'email' ? 'email' : 'tel',
                    showCancelButton: true,
                    confirmButtonColor: '#4ABDBD',
                    cancelButtonColor: '#e5e7eb',
                    confirmButtonText: t('pharmacycounter.send', 'Send'),
                    cancelButtonText: t('pharmacycounter.cancel', 'Cancel'),
                });
                if (prompt.isConfirmed && prompt.value) {
                    try {
                        const res = await send(String(prompt.value).trim());
                        toast.success(res?.data?.message || t('pharmacycounter.resent', 'A copy of the bill has been queued'));
                    } catch (retryErr) {
                        toast.warn(extractErrorMessage(retryErr, t('pharmacycounter.resend_error', 'Could not send the bill')));
                    }
                }
            } else {
                toast.warn(extractErrorMessage(err, t('pharmacycounter.resend_error', 'Could not send the bill')));
            }
        } finally {
            setResending(false);
        }
    }, [bill, api, t]);

    const nextPatient = useCallback(() => {
        setSelected(null);
        setDetail(null);
        setMode('prescription');
        resetSale();
        loadQueue({ quiet: true });
        setTimeout(() => searchInputRef.current?.focus(), 0);
    }, [resetSale, loadQueue]);

    /* -------------------------------------------------------------- *
     * Keyboard
     *
     * This is a counter, not a form. Everything the job needs is on a key, and
     * the handler is careful about which of them it takes back off the person
     * typing: the arrows only move the queue when focus is in the search box or
     * held with Alt, so keying a quantity never jumps the selection.
     * -------------------------------------------------------------- */

    useEffect(() => {
        const onKeyDown = (e) => {
            const tag = (e.target?.tagName || '').toLowerCase();
            const typing = tag === 'input' || tag === 'textarea' || tag === 'select';

            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
                return;
            }
            if (e.key === 'F4') {
                e.preventDefault();
                pickerInputRef.current?.focus();
                return;
            }
            if (e.key === 'F8' && bill) {
                e.preventDefault();
                printBill();
                return;
            }
            if (e.key === 'F9' || ((e.ctrlKey || e.metaKey) && e.key === 'Enter')) {
                e.preventDefault();
                if (bill) nextPatient(); else confirmDispense();
                return;
            }
            if (e.key === 'Escape') {
                if (bill) { e.preventDefault(); nextPatient(); return; }
                if (substituteFor) { e.preventDefault(); setSubstituteFor(null); return; }
                if (typing) e.target.blur();
                return;
            }

            // Alt+1..9 jumps straight to a line's quantity box. The numbers are
            // printed on the rows, so a pharmacist correcting the fourth item
            // does not have to tab past three batches, two discounts and a bin.
            if (e.altKey && /^[1-9]$/.test(e.key)) {
                const target = lines[Number(e.key) - 1];
                if (target) {
                    e.preventDefault();
                    const el = qtyRefs.current[target.key];
                    el?.focus();
                    el?.select();
                }
                return;
            }

            const inSearch = e.target === searchInputRef.current;
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && (inSearch || (e.altKey && !typing))) {
                if (!queue.length) return;
                e.preventDefault();
                setHighlight((h) => {
                    const next = e.key === 'ArrowDown'
                        ? Math.min(queue.length - 1, h + 1)
                        : Math.max(0, h - 1);
                    queueListRef.current
                        ?.querySelector(`[data-queue-index="${next}"]`)
                        ?.scrollIntoView({ block: 'nearest' });
                    return next;
                });
                return;
            }
            if (e.key === 'Enter' && inSearch && queue[highlight]) {
                e.preventDefault();
                guardedOpen(queue[highlight]);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [queue, highlight, lines, bill, substituteFor, printBill, nextPatient, confirmDispense, guardedOpen]);

    /* -------------------------------------------------------------- *
     * Render
     * -------------------------------------------------------------- */

    const paneHeight = 'lg:h-[calc(100vh-8.5rem)]';

    return (
        <div data-testid="pharmacy-counter-page" className="w-full flex flex-col gap-4">
            {/* ---------------------------------------------- header ---- */}
            <div className="bg-white rounded-2xl shadow-md p-4 flex flex-col xl:flex-row xl:items-center gap-3 justify-between">
                <div>
                    <h1 data-testid="pharmacy-counter-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] bg-clip-text text-transparent">
                        {t('pharmacycounter.title', 'Pharmacy counter')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('pharmacycounter.subtitle', 'Fulfil a prescription, or sell over the counter.')}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* AI pack (Phase 4, #15): read a paper prescription into
                        structured lines the pharmacist verifies. Hidden when the
                        hospital hasn't bought the pack. */}
                    {hasFeature('ai_prescription_ocr') && (
                        <button
                            type="button"
                            data-testid="pharmacy-ai-ocr-open"
                            onClick={() => setShowOcrPanel(true)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#4ABDBD]/10 text-[#2D8A8A] text-sm font-semibold hover:bg-[#4ABDBD]/20 transition-colors cursor-pointer"
                        >
                            <Sparkles className="w-4 h-4" />
                            {t('pharmacycounter.read_rx', 'Read prescription')}
                        </button>
                    )}
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
                        <StoreIcon className="w-4 h-4 text-[#2D8A8A]" />
                        <select
                            data-testid="pharmacy-store-select"
                            value={storeId}
                            onChange={(e) => setStoreId(e.target.value)}
                            disabled={storesLoading || stores.length === 0}
                            className="bg-transparent text-sm font-semibold text-gray-700 focus:outline-none cursor-pointer"
                        >
                            {stores.length === 0 && (
                                <option value="">{t('pharmacycounter.no_store', 'No counter configured')}</option>
                            )}
                            {stores.map((s) => (
                                <option key={s.storeId} value={s.storeId}>
                                    {s.name}{s.code ? ` (${s.code})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* OP vs IP is the single biggest lever on this screen: an
                        inpatient supply is part of a composite healthcare
                        service and carries no GST at all. It is a toggle rather
                        than a buried setting because getting it wrong is a
                        wrongly-taxed bill either way. */}
                    <div className="flex rounded-xl border border-gray-200 overflow-hidden">
                        {['OP', 'IP'].map((type) => (
                            <button
                                key={type}
                                type="button"
                                data-testid={`patient-type-${type}`}
                                onClick={() => { setPatientType(type); setDirty(true); }}
                                className={`px-4 py-2 text-sm font-bold cursor-pointer transition-colors ${patientType === type
                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white'
                                    : 'bg-white text-gray-500 hover:text-[#2D8A8A]'}`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        data-testid="walk-in-button"
                        onClick={startWalkIn}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm cursor-pointer transition-all ${mode === 'walkin'
                            ? 'bg-gradient-to-r from-[#D7A05C] to-[#c08e4c] text-white shadow-lg'
                            : 'border border-[#D7A05C] text-[#b07d38] hover:bg-[#D7A05C]/10'}`}
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {t('pharmacycounter.walk_in', 'Walk-in / OTC')}
                    </button>

                    <div
                        className="hidden xl:flex items-center gap-1.5 text-[11px] text-slate-400 pl-2"
                        title={t('pharmacycounter.keys_hint', 'F2 search · F4 add item · F9 confirm · F8 print · Esc next patient')}
                    >
                        <Keyboard className="w-4 h-4" />
                        <span className="font-mono">F2 · F4 · F9 · F8</span>
                    </div>
                </div>
            </div>

            <PrescriptionOcrPanel open={showOcrPanel} onClose={() => setShowOcrPanel(false)} />

            <div className="flex flex-col lg:flex-row gap-4">
                {/* ------------------------------------------ the queue ---- */}
                <aside className={`bg-white rounded-2xl shadow-md flex flex-col w-full lg:w-[350px] xl:w-[380px] shrink-0 ${paneHeight}`}>
                    <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-bold text-[#1a365d]">
                                {t('pharmacycounter.queue', 'Waiting')}
                                <span className="ml-2 text-xs font-semibold text-[#2D8A8A] bg-[#4ABDBD]/10 px-2 py-0.5 rounded-full">
                                    {queue.length}
                                </span>
                            </h2>
                            <button
                                type="button"
                                data-testid="queue-refresh"
                                onClick={() => loadQueue()}
                                title={refreshedAt
                                    ? t('pharmacycounter.refreshed_at', 'Updated {{time}}', { time: fmtTime(refreshedAt) })
                                    : t('pharmacycounter.refresh', 'Refresh')}
                                className="text-[#4ABDBD] hover:text-[#2D8A8A] p-1.5 rounded-lg hover:bg-[#4ABDBD]/10 cursor-pointer"
                            >
                                <RefreshCw className={`w-4 h-4 ${queueLoading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        <div className="relative">
                            <input
                                ref={searchInputRef}
                                data-testid="pharmacy-queue-search"
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                                placeholder={t('pharmacycounter.search_placeholder', 'Name / phone   (F2)')}
                                className="w-full p-2 pl-9 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                        </div>
                        {/* A search reaches back a month; the browse list is today
                            only. Saying so avoids the "it isn't there" phone call. */}
                        {debouncedSearch && (
                            <p className="text-[11px] text-slate-400 mt-2">
                                {t('pharmacycounter.search_scope', 'Searching the last 30 days of prescriptions.')}
                            </p>
                        )}
                    </div>

                    <div ref={queueListRef} className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[55vh] lg:max-h-none">
                        {queueLoading && queue.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10">
                                <CommonLoader />
                                <span className="text-sm text-[#4ABDBD]">{t('pharmacycounter.loading', 'Loading...')}</span>
                            </div>
                        ) : queueError ? (
                            <div className="p-4 text-center text-sm text-red-600 bg-red-50 rounded-xl">{queueError}</div>
                        ) : queue.length === 0 ? (
                            <div className="p-6 text-center text-sm text-gray-500">
                                {debouncedSearch
                                    ? t('pharmacycounter.no_match', 'Nobody matches that.')
                                    : t('pharmacycounter.queue_empty', 'Nothing waiting. Prescriptions written today appear here as soon as the doctor saves them.')}
                            </div>
                        ) : queue.map((entry, index) => {
                            const active = selected?.appointmentId === entry.appointmentId;
                            return (
                                <button
                                    key={entry.appointmentId}
                                    type="button"
                                    data-queue-index={index}
                                    data-testid={`queue-row-${entry.appointmentId}`}
                                    onClick={() => { setHighlight(index); guardedOpen(entry); }}
                                    className={`w-full text-left p-3 rounded-xl border transition-all cursor-pointer ${active
                                        ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 shadow-md'
                                        : index === highlight
                                            ? 'border-[#4ABDBD]/50 bg-gray-50'
                                            : 'border-gray-200 hover:border-[#4ABDBD]/50 hover:bg-gray-50'}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm text-gray-800 truncate">
                                                {entry.patientName || t('pharmacycounter.unnamed', 'Unnamed patient')}
                                            </div>
                                            <div className="text-[11px] text-slate-400 truncate">
                                                {[hospitalPatientCode(entry), entry.patientPhone].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                        {entry.status === 'partial' && (
                                            <span className="shrink-0 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">
                                                {t('pharmacycounter.partial', 'Partial')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                                        <span className="flex items-center gap-1 truncate">
                                            <User className="w-3 h-3 shrink-0" />{entry.doctorName || '—'}
                                        </span>
                                        <span className="flex items-center gap-1 shrink-0">
                                            <Clock className="w-3 h-3" />{fmtTime(entry.prescribedAt)}
                                        </span>
                                    </div>
                                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#4ABDBD]/10 text-[#2D8A8A] text-[10px] font-bold">
                                            <Pill className="w-3 h-3" />{entry.lineCount}
                                        </span>
                                        <span className="text-[11px] text-slate-400 truncate">
                                            {(entry.items || []).join(', ')}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* ------------------------------------------ the sale ---- */}
                <section className={`bg-white rounded-2xl shadow-md flex-1 min-w-0 flex flex-col overflow-hidden ${paneHeight}`}>
                    {bill ? (
                        <BillPanel
                            bill={bill}
                            onPrint={printBill}
                            onResend={resendBill}
                            resending={resending}
                            onNext={nextPatient}
                        />
                    ) : detailLoading ? (
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <CommonLoader />
                            <span className="text-sm text-[#4ABDBD]">{t('pharmacycounter.opening', 'Opening the prescription...')}</span>
                        </div>
                    ) : mode === 'prescription' && !selected ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                            <div className="w-16 h-16 rounded-2xl bg-[#4ABDBD]/10 flex items-center justify-center mb-4">
                                <Pill className="w-8 h-8 text-[#4ABDBD]" />
                            </div>
                            <h3 className="font-bold text-[#1a365d] mb-1">{t('pharmacycounter.pick_one', 'Pick somebody from the queue')}</h3>
                            <p className="text-sm text-slate-400 max-w-sm">
                                {t('pharmacycounter.pick_one_hint', 'Their prescription opens sorted by bin, so the items come off the shelf in one walk. For a counter sale with no prescription, start a walk-in.')}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* who this is for */}
                            <div className="p-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
                                {mode === 'walkin' ? (
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <span className="px-2 py-1 rounded-lg bg-[#D7A05C]/15 text-[#8a5f22] text-xs font-bold uppercase tracking-wide">
                                            {t('pharmacycounter.otc', 'Over the counter')}
                                        </span>
                                        <input
                                            data-testid="walk-in-name"
                                            value={walkIn.name}
                                            onChange={(e) => setWalkIn((w) => ({ ...w, name: e.target.value.slice(0, 60) }))}
                                            placeholder={t('pharmacycounter.walkin_name', 'Buyer name *')}
                                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                        />
                                        <input
                                            data-testid="walk-in-phone"
                                            value={walkIn.phone}
                                            onChange={(e) => setWalkIn((w) => ({ ...w, phone: e.target.value.replace(/[^\d+ ]/g, '').slice(0, 20) }))}
                                            placeholder={t('pharmacycounter.walkin_phone', 'Phone')}
                                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-40 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <div className="font-bold text-[#1a365d]">
                                            {detail?.patient?.name || selected?.patientName || '—'}
                                            {(hospitalPatientCode(detail?.patient) || hospitalPatientCode(selected)) && (
                                                <span className="ml-2 text-xs font-normal text-slate-400">
                                                    {hospitalPatientCode(detail?.patient) || hospitalPatientCode(selected)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {t('pharmacycounter.prescribed_by', 'Prescribed by')}{' '}
                                            <span className="font-semibold">{detail?.prescriber?.name || '—'}</span>
                                            {detail?.prescriber?.registrationNo && (
                                                <span className="text-slate-400"> · {t('pharmacycounter.reg_no', 'Reg')} {detail.prescriber.registrationNo}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {patientType === 'IP' && (
                                    <div className="text-[11px] px-3 py-1.5 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] font-semibold max-w-xs">
                                        {t('pharmacycounter.ip_exempt', 'Inpatient supply — billed GST-exempt as part of the admission.')}
                                    </div>
                                )}
                            </div>

                            {/* the lines */}
                            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                {lines.length === 0 ? (
                                    <div className="p-8 text-center text-sm text-gray-500">
                                        {t('pharmacycounter.no_lines', 'Nothing on this bill yet. Search below to add an item (F4).')}
                                    </div>
                                ) : priced.map((p, index) => (
                                    <DispenseLine
                                        key={p.line.key}
                                        index={index}
                                        priced={p}
                                        canDiscount={canDiscount}
                                        canSubstitute={canSubstitute}
                                        patientType={patientType}
                                        qtyRef={(el) => { qtyRefs.current[p.line.key] = el; }}
                                        onPatch={patchLine}
                                        onRemove={removeLine}
                                        onSubstitute={(key) => {
                                            setSubstituteFor(key);
                                            setTimeout(() => pickerInputRef.current?.focus(), 0);
                                        }}
                                    />
                                ))}
                            </div>

                            {/* add / substitute */}
                            <div className="border-t border-gray-100 p-3">
                                {substituteFor && (
                                    <div className="mb-2 flex items-center justify-between px-3 py-1.5 rounded-lg bg-[#D7A05C]/15 text-[#8a5f22] text-xs font-semibold">
                                        <span className="flex items-center gap-1.5">
                                            <Repeat className="w-3.5 h-3.5" />
                                            {t('pharmacycounter.substituting', 'Choosing a substitute — the original is kept on the record.')}
                                        </span>
                                        <button type="button" onClick={() => setSubstituteFor(null)} className="cursor-pointer hover:text-[#5c3f14]">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                                <div className="relative">
                                    <input
                                        ref={pickerInputRef}
                                        data-testid="pharmacy-item-search"
                                        value={pickerQuery}
                                        onChange={(e) => setPickerQuery(e.target.value.slice(0, 60))}
                                        placeholder={t('pharmacycounter.add_item', 'Scan a barcode or type a medicine to add   (F4)')}
                                        className="w-full p-2.5 pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                    />
                                    <Plus className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                                    {pickerLoading && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#4ABDBD] animate-spin" />}

                                    {pickerResults.length > 0 && (
                                        <div
                                            data-testid="pharmacy-item-results"
                                            className="absolute bottom-full left-0 right-0 mb-1 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl z-20"
                                        >
                                            {pickerResults.map((hit) => (
                                                <button
                                                    key={hit.medicineId}
                                                    type="button"
                                                    data-testid={`item-result-${hit.medicineId}`}
                                                    onClick={() => choosePickerResult(hit)}
                                                    className="w-full text-left px-3 py-2 hover:bg-[#4ABDBD]/10 flex items-center gap-3 cursor-pointer border-b border-gray-50 last:border-0"
                                                >
                                                    <span className="font-mono text-sm font-bold bg-slate-900 text-white px-2 py-1 rounded min-w-[64px] text-center">
                                                        {hit.pickBinCode || hit.homeBinCode || '—'}
                                                    </span>
                                                    <span className="flex-1 min-w-0">
                                                        <span className="block text-sm font-semibold text-gray-800 truncate">
                                                            {hit.name}
                                                            {hit.strength ? <span className="text-slate-400 font-normal"> {hit.strength}</span> : null}
                                                        </span>
                                                        <span className="block text-[11px] text-slate-400 truncate">
                                                            {[hit.genericName, hit.brand].filter(Boolean).join(' · ')}
                                                        </span>
                                                    </span>
                                                    <ScheduleBadge schedule={hit.schedule} />
                                                    <span className={`text-xs font-bold shrink-0 ${hit.inStock ? 'text-green-700' : 'text-red-500'}`}>
                                                        {hit.inStock
                                                            ? t('pharmacycounter.on_hand_n', '{{n}} on hand', { n: hit.qtySellable })
                                                            : t('pharmacycounter.out_of_stock', 'Out of stock')}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* totals + tender */}
                            <div className="border-t-2 border-gray-100 bg-gray-50/70 p-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
                                <TotalsPanel
                                    totals={totals}
                                    isInterState={isInterState}
                                    patientType={patientType}
                                    placeOfSupply={placeOfSupply}
                                    onInterState={(v) => { setIsInterState(v); setDirty(true); }}
                                    onPlaceOfSupply={setPlaceOfSupply}
                                />

                                <div className="flex flex-col gap-3">
                                    <PaymentPanel
                                        payments={payments}
                                        onPatch={patchPayment}
                                        onAdd={() => { paymentsTouched.current = true; setPayments((p) => [...p, { method: 'upi', amount: '', reference: '' }]); }}
                                        onRemove={(i) => { paymentsTouched.current = true; setPayments((p) => p.filter((_, idx) => idx !== i)); }}
                                        onExact={(i) => patchPayment(i, { amount: money(balanceDue + toNum(payments[i].amount)) })}
                                        balanceDue={balanceDue}
                                        overPaid={overPaid}
                                        missingReference={missingReference}
                                    />

                                    {blockedCount > 0 && (
                                        <div data-testid="dispense-blocked-note" className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2.5">
                                            <Ban className="w-4 h-4 shrink-0 mt-px" />
                                            <span>
                                                {t('pharmacycounter.blocked_lines', '{{n}} line(s) cannot be dispensed as they stand. Fix them or set the quantity to zero.', { n: blockedCount })}
                                            </span>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        data-testid="confirm-dispense-button"
                                        onClick={confirmDispense}
                                        disabled={!canConfirm}
                                        className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-white font-bold text-base shadow-lg transition-all ${canConfirm
                                            ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-xl cursor-pointer'
                                            : 'bg-gray-300 cursor-not-allowed'}`}
                                    >
                                        {submitting
                                            ? <Loader2 className="w-5 h-5 animate-spin" />
                                            : <CheckCircle2 className="w-5 h-5" />}
                                        {t('pharmacycounter.confirm', 'Dispense & bill')}
                                        <span className="text-sm font-semibold opacity-80">₹{money(totals.grandTotal)}</span>
                                        <span className="hidden sm:inline text-[11px] font-mono opacity-70 ml-1">F9</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * One line on the bill
 * ------------------------------------------------------------------ */

function DispenseLine({ index, priced, canDiscount, canSubstitute, patientType, qtyRef, onPatch, onRemove, onSubstitute }) {
    const { t } = useTranslation();
    const { line, chosen, unitMrp, qty, available, cost, blocks } = priced;
    const med = line.medicine;
    const expiryIn = daysUntil(chosen?.expiryDate);
    const expiringSoon = expiryIn !== null && expiryIn <= EXPIRY_WARNING_DAYS;
    const blocked = blocks.length > 0 && qty > 0;

    const dose = line.prescribed;
    const doseText = dose
        ? [
            dose.entryMode === 'S'
                ? [dose.morningCount, dose.afternoonCount, dose.eveningCount, dose.nightCount].map((n) => n || 0).join('-')
                : dose.dose,
            dose.foodTiming,
            dose.durationDays ? t('pharmacycounter.days', '{{n}} days', { n: dose.durationDays }) : null,
        ].filter(Boolean).join(' · ')
        : null;

    return (
        <div
            data-testid={`dispense-line-${line.key}`}
            className={`rounded-xl border p-3 ${blocked ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-[#4ABDBD]/40'}`}
        >
            <div className="flex gap-3">
                <BinBadge code={line.binCode} type={line.binType} testId={`bin-code-${line.key}`} />

                <div className="flex-1 min-w-0">
                    {/* what it is */}
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="font-bold text-gray-800 text-[15px] truncate">
                                <span className="text-slate-300 font-mono text-xs mr-1.5">{index + 1}</span>
                                {med?.name || line.prescribed?.medicineName || '—'}
                                {med?.strength && <span className="text-slate-400 font-normal ml-1">{med.strength}</span>}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {[med?.genericName, med?.brand, med?.form].filter(Boolean).join(' · ') || '—'}
                            </div>
                            {doseText && (
                                <div className="text-[11px] text-[#2D8A8A] mt-0.5 truncate">{doseText}</div>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <ScheduleBadge schedule={med?.schedule} />
                            {line.substitutedFromMedicineId && (
                                <span className="px-2 py-0.5 rounded-md bg-[#D7A05C]/20 text-[#8a5f22] text-[10px] font-bold uppercase">
                                    {t('pharmacycounter.substituted', 'Substituted')}
                                </span>
                            )}
                            {line.matchedOnGeneric && (
                                <span
                                    title={t('pharmacycounter.generic_match_hint', 'Matched on the salt, not the brand — check the pack.')}
                                    className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[10px] font-bold uppercase"
                                >
                                    {t('pharmacycounter.generic_match', 'By salt')}
                                </span>
                            )}
                            {canSubstitute && (
                                <button
                                    type="button"
                                    data-testid={`substitute-${line.key}`}
                                    onClick={() => onSubstitute(line.key)}
                                    title={t('pharmacycounter.substitute', 'Substitute')}
                                    className="p-1.5 rounded-lg text-[#2D8A8A] hover:bg-[#4ABDBD]/10 cursor-pointer"
                                >
                                    <Repeat className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                type="button"
                                data-testid={`remove-line-${line.key}`}
                                onClick={() => onRemove(line.key)}
                                title={t('pharmacycounter.remove', 'Remove')}
                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 cursor-pointer"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* batch · quantity · price */}
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 items-end">
                        <div className="col-span-2">
                            <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">
                                {t('pharmacycounter.batch', 'Batch')}
                            </label>
                            <select
                                data-testid={`batch-select-${line.key}`}
                                value={line.batchId}
                                onChange={(e) => onPatch(line.key, { batchId: e.target.value })}
                                disabled={line.batchesState === 'denied' || !(line.batches || []).length}
                                className={`tt-select w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${expiringSoon ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                            >
                                <option value="">
                                    {line.suggestedBatch
                                        ? `${line.suggestedBatch.batchNo} · ${fmtExpiry(line.suggestedBatch.expiryDate)} · ${t('pharmacycounter.fefo', 'FEFO')}`
                                        : t('pharmacycounter.no_batch', 'No sellable stock')}
                                </option>
                                {(line.batches || [])
                                    .filter((b) => b.batchId !== line.suggestedBatch?.batchId)
                                    .map((b) => (
                                        <option key={b.batchId} value={b.batchId}>
                                            {`${b.batchNo} · ${fmtExpiry(b.expiryDate)} · ${toNum(b.qtyOnHand)} ${t('pharmacycounter.left', 'left')}`}
                                        </option>
                                    ))}
                            </select>
                            <div className="text-[10px] mt-0.5 flex items-center gap-1">
                                {expiringSoon ? (
                                    <span className="text-amber-600 font-semibold flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        {t('pharmacycounter.expires_in', 'Expires in {{n}} days', { n: expiryIn })}
                                    </span>
                                ) : (
                                    <span className="text-slate-400">
                                        {t('pharmacycounter.on_hand', 'On hand')}: <b className="text-slate-600">{available}</b>
                                    </span>
                                )}
                                {line.batchesState === 'denied' && (
                                    <span
                                        className="text-slate-400"
                                        title={t('pharmacycounter.batch_locked_hint', 'Hand-picking a batch needs stock permissions. FEFO picks the earliest expiry, which is the same rule.')}
                                    >
                                        · {t('pharmacycounter.fefo_only', 'FEFO only')}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">
                                {t('pharmacycounter.rx_qty', 'Rx qty')}
                            </label>
                            <input
                                data-testid={`qty-prescribed-${line.key}`}
                                type="number" min="0" step="1"
                                value={line.qtyPrescribed}
                                onChange={(e) => onPatch(line.key, { qtyPrescribed: e.target.value })}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                            {/* The field holds what is STILL owed, so the course
                                it came out of is named here — otherwise "12" on
                                a 30-tablet course looks like a mistake. */}
                            {line.alreadyDispensed > 0 && (
                                <div className="text-[10px] text-amber-600 mt-0.5">
                                    {t('pharmacycounter.already', '{{n}} of {{total}} given earlier', {
                                        n: line.alreadyDispensed,
                                        total: line.fullCourseQty ?? '?',
                                    })}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wide text-[#2D8A8A] mb-1">
                                {t('pharmacycounter.give_qty', 'Give')}
                            </label>
                            <input
                                ref={qtyRef}
                                data-testid={`qty-input-${line.key}`}
                                type="number" min="0" step="1"
                                value={line.qtyDispensed}
                                onChange={(e) => onPatch(line.key, { qtyDispensed: e.target.value })}
                                className="w-full px-2 py-1.5 border-2 border-[#4ABDBD] rounded-lg text-sm font-bold text-right focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">
                                {t('pharmacycounter.mrp', 'MRP')}
                            </label>
                            <div className="px-2 py-1.5 text-sm text-right text-gray-700 font-semibold">
                                {unitMrp === null || unitMrp === undefined ? '—' : `₹${money(unitMrp)}`}
                            </div>
                        </div>

                        <div>
                            {/* The label is the switch. A percentage and a flat
                                rupee figure are the same control keyed two ways,
                                and giving each its own column would cost a third
                                of the row for a field used on one line in ten. */}
                            <button
                                type="button"
                                data-testid={`discount-mode-${line.key}`}
                                disabled={!canDiscount}
                                onClick={() => onPatch(line.key, {
                                    discountMode: line.discountMode === 'pct' ? 'amt' : 'pct',
                                    discountPercent: '',
                                    discountAmount: '',
                                })}
                                className={`block text-[10px] font-bold uppercase tracking-wide mb-1 ${canDiscount ? 'text-[#2D8A8A] hover:underline cursor-pointer' : 'text-slate-400'}`}
                            >
                                {line.discountMode === 'amt'
                                    ? t('pharmacycounter.discount_amt', 'Disc ₹')
                                    : t('pharmacycounter.discount_pct', 'Disc %')}
                            </button>
                            <input
                                data-testid={`discount-input-${line.key}`}
                                type="number" min="0" max={line.discountMode === 'amt' ? undefined : 100} step="0.01"
                                value={line.discountMode === 'amt' ? line.discountAmount : line.discountPercent}
                                disabled={!canDiscount}
                                onChange={(e) => onPatch(line.key, line.discountMode === 'amt'
                                    ? { discountAmount: e.target.value, discountPercent: '' }
                                    : { discountPercent: e.target.value, discountAmount: '' })}
                                title={canDiscount ? undefined : t('pharmacycounter.no_discount_right', 'Your role cannot reduce the counter price.')}
                                className={`w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${canDiscount ? '' : 'bg-gray-100 cursor-not-allowed'}`}
                            />
                        </div>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="text-[11px] text-slate-400">
                            {patientType === 'IP'
                                ? t('pharmacycounter.exempt_line', 'GST exempt (inpatient)')
                                : t('pharmacycounter.gst_line', 'GST {{rate}}% included in MRP', { rate: money(cost.gstRate) })}
                        </div>
                        <div data-testid={`line-total-${line.key}`} className="text-lg font-bold text-[#1a365d]">
                            ₹{money(cost.lineTotal)}
                        </div>
                    </div>

                    {(blocked || line.serverError) && (
                        <div data-testid={`line-block-${line.key}`} className="mt-2 flex items-start gap-2 text-xs text-red-700 bg-red-100/70 rounded-lg px-2.5 py-1.5">
                            <ShieldAlert className="w-4 h-4 shrink-0 mt-px" />
                            <span>{line.serverError || blocks[0]}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * Totals
 * ------------------------------------------------------------------ */

function TotalsPanel({ totals, isInterState, patientType, placeOfSupply, onInterState, onPlaceOfSupply }) {
    const { t } = useTranslation();
    // A minus belongs in front of the currency symbol, not inside the amount:
    // '₹-12.00' reads as a corrupted figure, '−₹12.00' reads as a deduction.
    const Row = ({ label, value, testId, strong }) => (
        <div className={`flex items-center justify-between ${strong ? 'text-base font-bold text-[#1a365d]' : 'text-sm text-slate-600'}`}>
            <span>{label}</span>
            <span data-testid={testId} className="font-semibold tabular-nums">
                {toNum(value) < 0 ? '−' : ''}₹{money(Math.abs(toNum(value)))}
            </span>
        </div>
    );

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-1.5">
            <Row label={t('pharmacycounter.gross', 'Gross (MRP)')} value={totals.subTotal} testId="totals-gross" />
            {totals.discountTotal > 0 && (
                <Row label={t('pharmacycounter.discount', 'Discount')} value={-totals.discountTotal} testId="totals-discount" />
            )}
            <Row label={t('pharmacycounter.taxable', 'Taxable value')} value={totals.taxableTotal} testId="totals-taxable" />
            {isInterState ? (
                <Row label={t('pharmacycounter.igst', 'IGST')} value={totals.igstTotal} testId="totals-igst" />
            ) : (
                <>
                    <Row label={t('pharmacycounter.cgst', 'CGST')} value={totals.cgstTotal} testId="totals-cgst" />
                    <Row label={t('pharmacycounter.sgst', 'SGST')} value={totals.sgstTotal} testId="totals-sgst" />
                </>
            )}
            {/* Disclosed as its own line because that is what a tax invoice has
                to do — the drawer settles in whole rupees and the difference is
                not allowed to hide inside a line's price. */}
            <Row label={t('pharmacycounter.round_off', 'Round off')} value={totals.roundOff} testId="totals-roundoff" />
            <div className="border-t border-gray-200 pt-2 mt-1">
                <Row label={t('pharmacycounter.net', 'Net payable')} value={totals.grandTotal} testId="totals-net" strong />
            </div>

            <div className="pt-2 mt-1 border-t border-gray-100 flex flex-wrap items-center gap-3">
                <label className={`flex items-center gap-2 text-xs ${patientType === 'IP' ? 'text-slate-300' : 'text-slate-500 cursor-pointer'}`}>
                    <input
                        type="checkbox"
                        data-testid="inter-state-toggle"
                        checked={isInterState}
                        disabled={patientType === 'IP'}
                        onChange={(e) => onInterState(e.target.checked)}
                        className="accent-[#4ABDBD] w-4 h-4"
                    />
                    {t('pharmacycounter.inter_state', 'Inter-state supply (IGST)')}
                </label>
                <input
                    data-testid="place-of-supply"
                    value={placeOfSupply}
                    onChange={(e) => onPlaceOfSupply(e.target.value.slice(0, 60))}
                    placeholder={t('pharmacycounter.place_of_supply', 'Place of supply')}
                    className="flex-1 min-w-[120px] px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                />
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * Tender
 * ------------------------------------------------------------------ */

function PaymentPanel({ payments, onPatch, onAdd, onRemove, onExact, balanceDue, overPaid, missingReference }) {
    const { t } = useTranslation();
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    {t('pharmacycounter.payment', 'Payment')}
                </span>
                <button
                    type="button"
                    data-testid="add-payment-row"
                    onClick={onAdd}
                    className="text-xs font-semibold text-[#2D8A8A] hover:underline cursor-pointer"
                >
                    + {t('pharmacycounter.split', 'Split')}
                </button>
            </div>

            {payments.map((p, i) => (
                // Keyed by position, not by method: the method is one of the
                // things this row edits, and a key that changes with it would
                // remount the amount box and take the focus with it mid-tender.
                <div key={`tender-${i}`} className="flex flex-wrap items-center gap-1.5">
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                        {PAYMENT_METHODS.map((m) => (
                            <button
                                key={m}
                                type="button"
                                data-testid={`payment-method-${i}-${m}`}
                                onClick={() => onPatch(i, { method: m })}
                                className={`px-2.5 py-1.5 text-[11px] font-bold uppercase cursor-pointer transition-colors ${p.method === m
                                    ? 'bg-[#4ABDBD] text-white'
                                    : 'bg-white text-slate-500 hover:text-[#2D8A8A]'}`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                    <input
                        data-testid={`payment-amount-${i}`}
                        type="number" min="0" step="0.01"
                        value={p.amount}
                        onChange={(e) => onPatch(i, { amount: e.target.value })}
                        placeholder="0.00"
                        className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <button
                        type="button"
                        data-testid={`payment-exact-${i}`}
                        onClick={() => onExact(i)}
                        className="px-2 py-1.5 text-[11px] font-bold text-[#2D8A8A] border border-[#4ABDBD] rounded-lg hover:bg-[#4ABDBD]/10 cursor-pointer"
                    >
                        {t('pharmacycounter.exact', 'Exact')}
                    </button>
                    {METHODS_NEEDING_REFERENCE.includes(p.method) && (
                        <input
                            data-testid={`payment-reference-${i}`}
                            value={p.reference}
                            onChange={(e) => onPatch(i, { reference: e.target.value.slice(0, 40) })}
                            placeholder={t('pharmacycounter.reference', 'Txn / ref *')}
                            className="flex-1 min-w-[110px] px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                    )}
                    {payments.length > 1 && (
                        <button type="button" onClick={() => onRemove(i)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg cursor-pointer">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            ))}

            {/* A bill left open is legitimate — a ward supply goes on the
                admission account and insurance settles later — so an unpaid
                balance is stated, not treated as an error. */}
            {overPaid ? (
                <div className="text-xs text-red-600 font-semibold">
                    {t('pharmacycounter.over_paid', 'That is more than the bill. Money above the total is change, not a payment.')}
                </div>
            ) : missingReference ? (
                <div className="text-xs text-amber-600 font-semibold">
                    {t('pharmacycounter.need_reference', 'A card, UPI or insurance payment needs its reference to reconcile at the end of the shift.')}
                </div>
            ) : balanceDue > 0 ? (
                <div data-testid="payment-balance" className="text-xs text-slate-500">
                    {t('pharmacycounter.balance_due', 'Balance left on the bill')}:{' '}
                    <b className="text-[#1a365d]">₹{money(balanceDue)}</b>
                </div>
            ) : null}
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * The bill, after the sale
 * ------------------------------------------------------------------ */

function BillPanel({ bill, onPrint, onResend, resending, onNext }) {
    const { t } = useTranslation();
    const invoice = bill?.invoice || {};
    const dispense = bill?.dispense || {};
    const items = bill?.items || [];
    const partial = dispense.status === 'partially_dispensed';

    return (
        <div data-testid="bill-panel" className="flex-1 overflow-y-auto p-6">
            <div className="max-w-2xl mx-auto">
                <div className="text-center">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mb-3">
                        <CheckCircle2 className="w-9 h-9" />
                    </div>
                    <h2 className="text-xl font-bold text-[#1a365d]">
                        {partial
                            ? t('pharmacycounter.partially_dispensed', 'Partially dispensed')
                            : t('pharmacycounter.dispensed_title', 'Dispensed')}
                    </h2>
                    {/* The invoice number is the thing the patient and the
                        hospital both refer back to, so it is the biggest text
                        on this panel. */}
                    <div data-testid="bill-invoice-no" className="mt-2 font-mono text-2xl font-black tracking-wide text-slate-900">
                        {invoice.invoiceNo || '—'}
                    </div>
                    {partial && (
                        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 inline-block">
                            {t('pharmacycounter.partial_note', 'Some items were short. The prescription stays on the queue so the balance can be collected later.')}
                        </p>
                    )}
                </div>

                <div className="mt-5 rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-[11px] uppercase tracking-wide text-slate-500">
                                <th className="text-left p-2">{t('pharmacycounter.item', 'Item')}</th>
                                <th className="text-left p-2">{t('pharmacycounter.batch', 'Batch')}</th>
                                <th className="text-right p-2">{t('pharmacycounter.qty', 'Qty')}</th>
                                <th className="text-right p-2">{t('pharmacycounter.amount', 'Amount')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.dispenseItemId} className="border-t border-gray-100">
                                    <td className="p-2 text-gray-700">{it.medicineName}</td>
                                    <td className="p-2 text-slate-500 text-xs">
                                        {it.batchNo} · {fmtExpiry(it.expiryDate)}
                                    </td>
                                    <td className="p-2 text-right tabular-nums">{toNum(it.qtyDispensed)}</td>
                                    <td className="p-2 text-right tabular-nums font-semibold">₹{money(it.lineTotal)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-gray-200 bg-gray-50">
                                <td colSpan={3} className="p-2 text-right font-bold text-[#1a365d]">
                                    {t('pharmacycounter.net', 'Net payable')}
                                </td>
                                <td className="p-2 text-right font-bold text-[#1a365d] tabular-nums">
                                    ₹{money(invoice.grandTotal)}
                                </td>
                            </tr>
                            {toNum(invoice.balance) > 0 && (
                                <tr className="bg-amber-50">
                                    <td colSpan={3} className="p-2 text-right text-xs font-semibold text-amber-700">
                                        {t('pharmacycounter.outstanding', 'Outstanding')}
                                    </td>
                                    <td className="p-2 text-right text-xs font-bold text-amber-700 tabular-nums">
                                        ₹{money(invoice.balance)}
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>

                <div className="mt-5 flex flex-wrap gap-2 justify-center">
                    <button
                        type="button"
                        data-testid="print-bill-button"
                        onClick={onPrint}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white font-semibold text-sm shadow-lg hover:shadow-xl cursor-pointer"
                    >
                        <Printer className="w-4 h-4" />
                        {t('pharmacycounter.print', 'Print bill')}
                        <span className="text-[11px] font-mono opacity-70">F8</span>
                    </button>
                    <button
                        type="button"
                        data-testid="resend-bill-email"
                        onClick={() => onResend('email')}
                        disabled={resending}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer disabled:opacity-50"
                    >
                        {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {t('pharmacycounter.email_bill', 'Email')}
                    </button>
                    <button
                        type="button"
                        data-testid="resend-bill-sms"
                        onClick={() => onResend('sms')}
                        disabled={resending}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer disabled:opacity-50"
                    >
                        <Send className="w-4 h-4" />
                        {t('pharmacycounter.sms_bill', 'SMS')}
                    </button>
                    <button
                        type="button"
                        data-testid="next-patient-button"
                        onClick={onNext}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white font-semibold text-sm cursor-pointer hover:bg-slate-800"
                    >
                        {t('pharmacycounter.next_patient', 'Next patient')}
                        <span className="text-[11px] font-mono opacity-70">Esc</span>
                    </button>
                </div>

                {/* Re-sending never re-issues. The invoice number, its lines and
                    its PDF are reused exactly — a second number for one supply
                    would show in the GST register as two sales. */}
                <p className="mt-3 text-center text-[11px] text-slate-400">
                    {t('pharmacycounter.resend_note', 'Sending again delivers another copy of this same bill — it never raises a second one.')}
                </p>
            </div>
        </div>
    );
}
